import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import { Plus, Trash2, Loader2, Users as UsersIcon, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ROLES = ["admin", "analis", "viewer"];

const ROLE_STYLE = {
  admin: "bg-slate-900 text-white",
  analis: "bg-amber-100 text-amber-900",
  viewer: "bg-slate-100 text-slate-700",
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });

  // Password reset dialog state
  const [pwTarget, setPwTarget] = useState(null); // user object or null
  const [pwForm, setPwForm] = useState({ new_password: "", confirm: "" });
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/users", form);
      toast.success("User dibuat");
      setForm({ name: "", email: "", password: "", role: "viewer" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try {
      await api.delete(`/users/${id}`);
      setUsers((u) => u.filter((x) => x.id !== id));
      toast.success("User dihapus");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPwDialog = (user) => {
    setPwTarget(user);
    setPwForm({ new_password: "", confirm: "" });
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setPwSubmitting(true);
    try {
      await api.post(`/users/${pwTarget.id}/password`, { new_password: pwForm.new_password });
      toast.success(`Password ${pwTarget.email} berhasil diganti`);
      setPwTarget(null);
      setPwForm({ new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSubmitting(false);
    }
  };

  return (
    <div className="px-6 py-6 space-y-5" data-testid="users-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Administrasi</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <UsersIcon className="h-6 w-6 text-slate-500" /> Manajemen Pengguna
          </h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none" data-testid="add-user-button">
              <Plus className="h-4 w-4 mr-2" /> Tambah User
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm" data-testid="add-user-dialog">
            <DialogHeader><DialogTitle>Buat Akun Baru</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Nama</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 rounded-sm border-slate-300" data-testid="user-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 rounded-sm border-slate-300" data-testid="user-email-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="mt-1 rounded-sm border-slate-300" data-testid="user-password-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Role</Label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="mt-1 w-full rounded-sm border border-slate-300 bg-white text-sm px-2 py-1.5"
                  data-testid="user-role-select"
                >
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={adding} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none" data-testid="submit-user-button">
                  {adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Nama</th>
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</th>
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Role</th>
                <th className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5 font-medium flex items-center gap-2">
                    {u.role === "admin" && <ShieldCheck className="h-4 w-4 text-amber-500" />}
                    {u.name}
                  </td>
                  <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded-sm ${ROLE_STYLE[u.role] || ""}`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openPwDialog(u)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-slate-300 text-slate-500 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300 transition-colors"
                        title="Ganti Password"
                        data-testid={`change-password-${u.id}`}
                      >
                        <KeyRound className="h-3.5 w-3.5" />
                      </button>
                      {u.id !== me.id && (
                        <button
                          onClick={() => del(u.id)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-slate-300 text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
                          title="Hapus User"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Password reset dialog (admin → any user) */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) setPwTarget(null); }}>
        <DialogContent className="rounded-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Ganti Password</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Reset password untuk <b className="text-slate-900">{pwTarget?.name}</b>
              {" "}<span className="font-mono text-xs">({pwTarget?.email})</span>.
              Lockout otomatis akan dibersihkan setelah berhasil.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPw} className="space-y-3">
            <div>
              <Label htmlFor="new-pw" className="text-xs uppercase tracking-wider text-slate-600">Password Baru</Label>
              <Input
                id="new-pw"
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                required
                minLength={6}
                placeholder="Minimal 6 karakter"
                className="mt-1 rounded-sm border-slate-300"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm-pw" className="text-xs uppercase tracking-wider text-slate-600">Konfirmasi Password</Label>
              <Input
                id="confirm-pw"
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                required
                minLength={6}
                placeholder="Ulangi password baru"
                className="mt-1 rounded-sm border-slate-300"
                data-testid="confirm-password-input"
              />
              {pwForm.confirm && pwForm.new_password !== pwForm.confirm && (
                <p className="mt-1 text-[11px] text-red-600">Password tidak cocok</p>
              )}
            </div>
            {pwTarget?.id === me.id && (
              <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                ⚠️ Anda sedang mengganti password akun Anda sendiri. Setelah disimpan, gunakan password baru saat login berikutnya.
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPwTarget(null)}
                className="rounded-sm border-slate-300"
                data-testid="cancel-reset-password-button"
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={pwSubmitting}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none"
                data-testid="submit-reset-password-button"
              >
                {pwSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Simpan Password Baru
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
